import { isNil, get, size, isUndefined, sortBy } from 'lodash';
import moment from 'moment';
import React from 'react';
import { renderDatePicker } from '../../screens/ChargerDetails/components/ChargerDetailsFormRenderers/renderDatePicker';
import { renderGooglePlaceAutocomplete } from '../../screens/ChargerDetails/components/ChargerDetailsFormRenderers/renderGooglePlaceAutocomplete';
import { renderImageLoader } from '../../screens/ChargerDetails/components/ChargerDetailsFormRenderers/renderImageLoader';
import {
  renderCustomerNameField,
  renderReadOnlyTextField,
  renderOnlineComputedField,
} from '../../screens/ChargerDetails/components/ChargerDetailsFormRenderers/renderReadOnlyTextField';
import { renderSelectField } from '../../screens/ChargerDetails/components/ChargerDetailsFormRenderers/renderSelectField';
import { renderTextField } from '../../screens/ChargerDetails/components/ChargerDetailsFormRenderers/renderTextField';
import InfoButtonComponent from '../../screens/HomePage/components/InfoButtonComponent';
import { isChargerTelemetryAvailable } from '../components/appConfig/selector';
import RenderConnectorStates, { AlertConnectorStates } from '../components/Form/RenderConnectorStates';
import MappedLabel from '../components/MappedLabel/MappedLabel';
import MenuItem from '../components/MenuItem/MenuItem';
import { cConditionOptions, cChargingOptions, statusMenuItems } from '../constants/CommonUsedDropdownsStatics';
import hasAccess, { viewChargerConfigsId, viewChargerCustomer, viewChargerTelemetry } from './HasAccess';
import StringUtils from './StringUtils';
import { datePickerDate, iotOnDateFormat, isCountryValid, isIdentityNotEmpty, required, uniqCName } from './ValidationRules';
import { fullPlazaNameLabel } from '../constants/naming';
import {
  CHARGER_STATUS_ALERT,
  SYSTEM_GENERATED_ADDRESS,
  SYSTEM_GENERATED_CHARGER_NAME,
  SYSTEM_GENERATED_COUNTRY,
} from '../constants/statuses';
import { getShowTechnicalInfoFieldsFlag } from './AuthenticationRules';
import uiLabelsPaths from './uiFieldLabelsDictionary';

export const coordinatesFormat = (value) => {
  const delimiter = StringUtils.getDelimiterForCurrentLocale();

  return isNil(value) ? '' : value.toString().replace(/[.,]/g, delimiter);
};

export const coordinatesValuePostProcessor = (value) => {
  let indexForCommaOrDot = 0;
  let indexForMinus = 0;
  const delimiter = StringUtils.getDelimiterForCurrentLocale();

  return isNil(value)
    ? ''
    : value.toString().replace(/[^0-9]/g, (match) => {
        if (match === '.' || match === ',') {
          // should be only one dot or comma in coordinate, others are replaced on empty symbol
          return indexForCommaOrDot++ === 0 ? delimiter : '';
        }
        if (match === '-') {
          // should be only one minus in coordinate, others are replaced on empty symbol
          return indexForMinus++ === 0 ? '-' : '';
        }

        return '';
      });
};

export const dateValueFormatter = (value) => (value ? moment.utc(value).format(iotOnDateFormat) : '');

export const coordinatesNormalize = (value) => (isNil(value) ? '' : value.toString().replace(',', '.'));

export const certificationStatuses = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'REVOKED', label: 'Revoked' },
];

const validationMap = {
  active: {
    c_name: {
      validate: [required],
      starred: true,
    },
    c_country: {
      validate: [required],
      starred: true,
    },
    c_address: {
      starred: true,
      validate: [required, isIdentityNotEmpty, isCountryValid],
    },
    c_latitude: {
      validate: [required],
      starred: true,
    },
    c_longitude: {
      validate: [required],
      starred: true,
    },
    c_utc: {
      validate: [required],
      starred: true,
    },
  },
  inactive: {
    c_name: {
      starred: false,
      validate: [],
    },
    c_country: {
      starred: false,
      validate: [],
    },
    c_address: {
      starred: false,
      validate: [],
    },
    c_latitude: {
      starred: false,
      validate: [],
    },
    c_longitude: {
      starred: false,
      validate: [],
    },
    c_utc: {
      starred: false,
      validate: [],
    },
  },
};

const buildFieldPropsGetter = (sectionName, config, mode) => (fieldName) => {
  const props = {};
  const forbiddenProps = ['name', 'component'];
  const fieldProps = get(config, `chargerDetails.${sectionName}.fieldProps`);

  if (fieldProps && fieldProps[fieldName]) {
    Object.keys(fieldProps[fieldName]).forEach((prop) => {
      if (!forbiddenProps.includes(prop)) {
        if (prop === 'readOnly') {
          props[prop] = mode === 'view' || fieldProps[fieldName][prop];
        } else {
          props[prop] = fieldProps[fieldName][prop];
        }
      }
    });
  }

  return props;
};

const defaultConnectorsNumberToShow = (config) => (get(config, 'chargerDetails.state.blankConnectors', true) ? 4 : 2);

export const getConnectorsNumberToShow = (config) =>
  get(config, 'chargerDetails.state.connectorsNumber', defaultConnectorsNumberToShow(config));

class ChargerFormBuilidingService {
  buildTabsWithMode(mode, currentChargerData, config, permissions) {
    const uiLabels = get(config, 'ui_labels');
    const country = get(uiLabels, 'c_identity/c_country');
    const status = get(uiLabels, 'i2p2_state/c_status');
    const condition = get(uiLabels, 'i2p2_state/c_condition');
    const warrantyEnd = get(uiLabels, 'c_exploitation_info/c_warranty_end');
    const vendorSerial = get(uiLabels, 'c_identity/vendor[0]/v_values/vendor_serial');
    const productSerial = get(uiLabels, 'c_identity/product_serial_number');
    const contact = get(uiLabels, 'c_exploitation_info/c_contact');
    const notes = get(uiLabels, 'c_exploitation_info/notes');
    const utc = get(uiLabels, 'c_identity/c_utc');
    const maintainer = get(uiLabels, 'c_exploitation_info/c_maintainer');
    const address = get(uiLabels, 'c_identity/c_address');
    const productLevel = get(uiLabels, 'c_technical_information/c_product_level');
    const supervisor = get(uiLabels, 'c_exploitation_info/c_supervisor');
    const enclosure = get(uiLabels, 'c_technical_information/c_enclosure');
    const longitude = get(uiLabels, 'c_identity/c_longitude');
    const latitude = get(uiLabels, 'c_identity/c_latitude');
    const owner = get(uiLabels, 'c_exploitation_info/c_owner');
    const nickname = get(uiLabels, 'c_exploitation_info/c_nickname');
    const pictureKeys = get(uiLabels, 'c_identity/picture_keys');
    const name = get(uiLabels, 'c_identity/c_name');
    const uuid = get(uiLabels, 'c_uuid');
    const type = get(uiLabels, uiLabelsPaths.p_type);
    const online = get(uiLabels, 'i2p2_state/c_online');
    const moduleStatus = get(uiLabels, 'c_full_state/vendor/module_status');
    const connectorState = get(uiLabels, 'c_full_state/connector_state');
    const productVersion = get(uiLabels, 'c_technical_information/c_product_version');
    const isOnline = get(currentChargerData, 'i2p2_state.c_online');
    const systemGeneratedValues = get(currentChargerData, 'i2p2_state.system_generated_values', []);
    const getConnector = (index) => get(currentChargerData, 'c_full_state', []).find((item) => item.connector_id === index);

    const getStateTab = (i) => {
      const moduleErrors = get(currentChargerData, `c_full_state[${i}].module_errors`);
      const connectorStatus = get(currentChargerData, `i2p2_state.ports[${i}].p_status`);
      const connectorInAlert = connectorStatus === CHARGER_STATUS_ALERT.toUpperCase() && !moduleErrors;
      const infoOption = i === 0 ? 'firstConnectorState' : 'otherConnectorState';

      const section = {
        label: `Connector ${i}`,
        value: `port_${i}`,
        fields: [
          {
            name: `c_i2p2_configuration.c_port_names[${i}]`,
            component: renderTextField,
            readOnly: true,
            floatingLabelText: get(uiLabels, `c_i2p2_configuration/c_port_names[${i}]`, `Connector ${i} Name`),
            type: 'text',
            validate: [uniqCName(i)],
            fixedValue: i === 0 ? 'Station' : `Connector ${i}`,
          },
          {
            name: `i2p2_state.ports[${i}].p_status`,
            component: renderSelectField,
            className: connectorInAlert ? 'connector-state__select' : 'staticField',
            floatingLabelText: (
              <div className="connector-state__label">
                {get(uiLabels, `i2p2_state/ports[${i}]/p_status`, `Connector ${i} State`)}
                <InfoButtonComponent infoOption={infoOption} />
              </div>
            ),
            children: connectorInAlert
              ? AlertConnectorStates(!connectorInAlert || mode === 'view' || !isOnline)
              : RenderConnectorStates(!connectorInAlert || mode === 'view' || !isOnline),
            readOnly: !connectorInAlert || mode === 'view' || !isOnline,
            isUnknownValue: !isOnline,
          },
        ],
      };

      if (size(get(currentChargerData, 'c_full_state'))) {
        const connector = getConnector(i);

        if (connector && connector.vendor && connector.vendor.length > 0) {
          connector.vendor.forEach((module, index) => {
            if (module.module_name.toLowerCase() !== 'monitoring_link') {
              section.fields.push({
                name: `c_full_state[${i}].vendor[${index}].module_status`,
                floatingLabelText: <MappedLabel>{module.module_name}</MappedLabel>,
                component: renderReadOnlyTextField,
                className: 'staticField',
                readOnly: true,
              });
            }
          });
        }
      }

      return section;
    };

    const fillSessionTab = (basicTabsSet) => {
      const sectionsTab = basicTabsSet.find((tab) => tab.value === 'state');
      const connectorsCount = getConnectorsNumberToShow(config);

      for (let i = 0; i < connectorsCount; i++) {
        sectionsTab.sections.push(getStateTab(i));
      }
    };
    const getLabel = (nameFromSettings, defaultName) => (!isUndefined(nameFromSettings) ? nameFromSettings : defaultName);

    const modifyDetailsTab = (basicTabsSet, config) => {
      const sectionsTab = basicTabsSet.find((tab) => tab.value === 'details');

      const getConfiguredFiledProps = buildFieldPropsGetter('details', config, mode);

      sectionsTab.sections.forEach((section, sectionPos) => {
        section.fields.forEach((field, fieldPos) => {
          const props = getConfiguredFiledProps(field.name);

          sectionsTab.sections[sectionPos].fields[fieldPos] = { ...field, ...props };
        });
      });
    };

    const getMonitoringLinkPath = () => {
      const connector0 = getConnector(0);

      if (connector0) {
        const connector0Index = get(currentChargerData, 'c_full_state', []).indexOf(connector0);

        if (connector0.vendor) {
          const monitoringLink = connector0.vendor.find((module) => module.module_name.toLowerCase() === 'monitoring_link');
          const moduleStatus = get(monitoringLink, 'module_status', '');

          if (monitoringLink && moduleStatus.toLowerCase() !== 'unknown') {
            const monitoringLinkIndex = connector0.vendor.indexOf(monitoringLink);

            return `c_full_state[${connector0Index}].vendor[${monitoringLinkIndex}].module_status`;
          }
        }
      }

      return '';
    };

    const exploitationInfoFields = [];

    if (hasAccess(viewChargerCustomer, permissions, config)) {
      exploitationInfoFields.unshift({
        name: 'customer.customerName',
        component: renderCustomerNameField,
        readOnly: true,
        floatingLabelText: 'Customer Name',
      });
    }

    if (config.plazaAvailabilityMode === 'full' || config.plazaAvailabilityMode === 'partially') {
      exploitationInfoFields.push({
        name: 'plaza_name',
        component: renderTextField,
        multiLine: true,
        floatingLabelText: fullPlazaNameLabel,
        readOnly: true,
        type: 'text',
      });
    }

    exploitationInfoFields.push(
      {
        name: 'c_exploitation_info.c_owner',
        component: renderSelectField,
        readOnly: mode === 'view',
        nullable: true,
        floatingLabelText: getLabel(owner, 'Owner'),
      },
      {
        name: 'c_exploitation_info.c_supervisor',
        component: renderSelectField,
        readOnly: mode === 'view',
        nullable: true,
        floatingLabelText: getLabel(supervisor, 'Supervisor'),
      },
      {
        name: 'c_exploitation_info.c_maintainer',
        component: renderSelectField,
        readOnly: mode === 'view',
        nullable: true,
        floatingLabelText: getLabel(maintainer, 'Maintainer'),
      },
      {
        name: 'c_exploitation_info.c_nickname',
        component: renderTextField,
        readOnly: mode === 'view',
        floatingLabelText: getLabel(nickname, 'Nickname'),
        type: 'text',
        multiLine: true,
        minRows: 1,
        maxRows: 3,
      },
      {
        name: 'c_exploitation_info.c_warranty_end',
        component: renderDatePicker,
        className: 'contents',
        timestampOutput: true,
        isUtc: true,
        floatingLabelText: getLabel(warrantyEnd, 'Warranty End'),
        validate: [datePickerDate],
        readOnly: mode === 'view',
      },
      {
        name: 'c_exploitation_info.c_contact',
        component: renderTextField,
        readOnly: mode === 'view',
        floatingLabelText: getLabel(contact, 'Contact'),
        type: 'text',
        multiLine: true,
        minRows: 1,
        maxRows: 3,
      },
      {
        name: 'c_exploitation_info.notes',
        component: renderTextField,
        floatingLabelText: getLabel(notes, 'Notes'),
        readOnly: mode === 'view',
        type: 'text',
        multiLine: true,
        minRows: 1,
        maxRows: 3,
      },
    );

    if (getShowTechnicalInfoFieldsFlag(config)) {
      exploitationInfoFields.push(
        {
          name: 'c_technical_information.c_product_version',
          parent: uiLabelsPaths.p_type,
          validate: [],
          component: renderSelectField,
          readOnly: mode === 'view',
          floatingLabelText: getLabel(productVersion, 'Product Version'),
          nullable: true,
          className: 'charger-details-form-select',
        },
        {
          name: 'c_technical_information.c_enclosure',
          validate: [],
          component: renderSelectField,
          readOnly: mode === 'view',
          floatingLabelText: getLabel(enclosure, 'Enclosure'),
          nullable: true,
          className: 'charger-details-form-select',
        },
        {
          name: 'c_technical_information.c_product_level',
          validate: [],
          component: renderSelectField,
          readOnly: mode === 'view',
          floatingLabelText: getLabel(productLevel, 'Product Level'),
          nullable: true,
          className: 'charger-details-form-select',
        },
      );
    }

    const validation = validationMap[get(currentChargerData, 'i2p2_state.c_condition')] || validationMap.inactive;

    const chargerStatusReadOnly = get(currentChargerData, 'i2p2_state.c_status') !== 'MONITORED' || mode === 'view' || !isOnline;

    const basicTabsSet = [
      {
        label: 'Details',
        value: 'details',
        type: 'sections',
        sections: [
          {
            label: 'General Information',
            value: 'gen_info',
            fields: [
              {
                name: 'c_uuid',
                component: renderReadOnlyTextField,
                floatingLabelText: getLabel(uuid, 'Charger ID'),
                type: 'text',
              },
              {
                name: 'c_identity.c_name',
                component: renderTextField,
                floatingLabelText: getLabel(name, 'Charger Name'),
                readOnly: mode === 'view',
                type: 'text',
                addressPendingRequired: true,
                autoActivated: systemGeneratedValues.includes(SYSTEM_GENERATED_CHARGER_NAME),
                ...validation.c_name,
              },
              {
                name: 'firmware_version',
                readOnly: true,
                component: renderReadOnlyTextField,
                floatingLabelText: 'FW Version',
              },
              {
                name: uiLabelsPaths.p_type,
                component: renderTextField,
                readOnly: true,
                floatingLabelText: getLabel(type, 'Product Type'),
              },
              {
                name: 'vendorData.vendor_serial',
                component: renderTextField,
                floatingLabelText: getLabel(vendorSerial, 'Company Serial Number'),
                readOnly: mode === 'view',
                type: 'text',
              },
              {
                name: 'c_identity.product_serial_number',
                component: renderTextField,
                floatingLabelText: getLabel(productSerial, 'Product Serial Number'),
                readOnly: mode === 'view',
                type: 'text',
              },
              {
                name: 'i2p2_state.c_online_computed',
                component: renderOnlineComputedField,
                floatingLabelText: getLabel(online, 'Online'),
              },
              {
                name: 'charger_state',
                component: renderSelectField,
                floatingLabelText: getLabel(connectorState, 'Present Charger State'),
                children: cChargingOptions(),
                readOnly: true,
                isUnknownValue: !isOnline,
              },
              {
                name: 'i2p2_state.c_condition',
                validate: [required],
                component: renderSelectField,
                floatingLabelText: getLabel(condition, 'Charger Condition'),
                children: cConditionOptions(mode, true, mode === 'view', get(currentChargerData, 'i2p2_state.c_condition') === 'pending'),
                readOnly: mode === 'view',
                addressIsRequired: true,
              },
              {
                name: getMonitoringLinkPath(),
                component: renderReadOnlyTextField,
                floatingLabelText: getLabel(moduleStatus, 'Charger Communication'),
                readOnly: true,
                customStyle: 'upperCaseFirstLetter',
                isUnknownValue: !isOnline,
                hidden: true,
              },
              {
                name: 'i2p2_state.c_status',
                component: renderSelectField,
                floatingLabelText: (
                  <div className="label--withInfo">
                    {getLabel(status, 'Charger Status')}
                    <InfoButtonComponent infoOption="chargerStatuses" isAbsolute={false} />
                  </div>
                ),
                children: statusMenuItems(get(currentChargerData, 'i2p2_state.c_status') === 'MONITORED', chargerStatusReadOnly),
                readOnly: chargerStatusReadOnly,
                starred: true,
                isUnknownValue: !isOnline,
              },
            ],
          },
          {
            label: 'Localization Information',
            value: 'loc_info',
            fields: [
              {
                name: 'c_identity.c_country',
                readOnly: mode === 'view',
                component: renderSelectField,
                floatingLabelText: getLabel(country, 'Country'),
                nullable: true,
                autoActivated: systemGeneratedValues.includes(SYSTEM_GENERATED_COUNTRY),
                ...validation.c_country,
              },
              {
                name: 'c_identity.c_address',
                readOnly: mode === 'view',
                component: renderGooglePlaceAutocomplete,
                className: 'render-charger-details-google-autocomplete',
                floatingLabelText: getLabel(address, 'Address'),
                type: 'text',
                multiLine: true,
                countryIsRequired: true, // The field will be staying read-only while the country not selected
                minRows: 1,
                maxRows: 3,
                autoActivated: systemGeneratedValues.includes(SYSTEM_GENERATED_ADDRESS),
                ...validation.c_address,
              },
              {
                name: 'c_identity.c_latitude',
                readOnly: true,
                component: renderTextField,
                // floatingLabelText: 'Latitude',
                format: coordinatesFormat,
                valuePostProcessor: coordinatesValuePostProcessor,
                normalize: coordinatesNormalize,
                type: 'text',
                floatingLabelText: getLabel(latitude, 'Latitude'),
                ...validation.c_latitude,
              },
              {
                name: 'c_identity.c_longitude',
                readOnly: true,
                component: renderTextField,
                // floatingLabelText: 'Longitude',
                format: coordinatesFormat,
                valuePostProcessor: coordinatesValuePostProcessor,
                normalize: coordinatesNormalize,
                type: 'text',
                floatingLabelText: getLabel(longitude, 'Longitude'),
                ...validation.c_longitude,
              },
              {
                name: 'c_identity.c_utc',
                component: renderTextField,
                floatingLabelText: getLabel(utc, 'UTC Offset'),
                type: null,
                readOnly: mode === 'view',
                ...validation.c_utc,
              },
              {
                name: 'c_identity.picture_keys',
                component: renderImageLoader,
                floatingLabelText: getLabel(pictureKeys, 'Picture'),
                subLabel: '(.PNG, .BMP, .JPEG, .JPG, .GIF images only)',
                type: null,
                readOnly: mode === 'view',
              },
            ],
          },
          {
            label: 'Exploitation Information',
            value: 'exp_info',
            fields: exploitationInfoFields,
          },
        ],
      },
      {
        label: 'Telemetry',
        value: 'telemetry',
        type: 'telemetry',
        hidden: !(isChargerTelemetryAvailable(config, '') && hasAccess(viewChargerTelemetry, permissions)),
        sections: [],
        tabItems: [
          {
            label: 'Historical Data',
            value: 'telemetry',
          },
          {
            label: 'Real Time Data',
            value: 'telemetry/realtime',
          },
        ],
      },
      ...(get(config, 'chargerDetails.configuration.show', false)
        ? [
            {
              label: 'Configuration',
              value: 'configuration',
              type: 'sections-subsections',
              className: 'configuration-tab',
              sections: [],
              hidden: !hasAccess(viewChargerConfigsId, permissions),
            },
          ]
        : []),
      {
        label: 'Packages',
        value: 'packages',
        type: 'sections',
        sections: [],
      },
      {
        label: 'State',
        value: 'state',
        type: 'sections',
        sections: [],
      },
    ];

    modifyDetailsTab(basicTabsSet, config);

    fillSessionTab(basicTabsSet);

    return basicTabsSet;
  }
}

// Prepare charger's connectors dropdown options
export const connectorIdOptions = (charger, config, showConnector0 = false) => {
  const portNames = get(charger, 'c_i2p2_configuration.c_port_names');
  const connectorsCount = getConnectorsNumberToShow(config);
  const options = [];
  const allMenuItem = (
    <MenuItem key="All" value="" title="All">
      All
    </MenuItem>
  );

  for (let i = showConnector0 ? 0 : 1; i < connectorsCount; i++) {
    const label = portNames[i] || `Connector ${i}`;

    options.push(
      <MenuItem key={i} value={i} title={label}>
        {label}
      </MenuItem>,
    );
  }

  return [allMenuItem, ...sortBy(options, [(item) => item.props.children && item.props.children.toLowerCase()])];
};

// options: { value: any, label: string }[]
export const makeSelectInputOptions = (options) =>
  options.map(({ value, label }) => (
    <MenuItem key={`${value}-${label}`} value={value} title={label}>
      {' '}
      {label}{' '}
    </MenuItem>
  ));

export default new ChargerFormBuilidingService();
